import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  Button,
  Card,
  CardContent,
  CircularProgress,
  AppBar, 
  Toolbar,
} from "@mui/material";
import axios from "axios";
import Swal from "sweetalert2";

const EditRolePermission = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [role, setRole] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const userDataString = localStorage.getItem("Login_User_Community_Details");
  const token = JSON.parse(userDataString);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  
  useEffect(() => {
    setLoading(true);
  
    // Fetch role data
    axios
      .get(`${API_BASE_URL}/api/getRoleByIdCatSub/${id}`)
      .then((response) => {
        const roleData = response.data;
  
        setRole(roleData);
  
        // Debug the roleData structure
        console.log("Role Data:", roleData);
  
        // Populate selectedCategories based on the API response
        const selected = {};
        roleData.categories?.forEach((category) => {
          selected[category.id] = Object.values(category.subcategories || {}).map(
            (sub) => sub.subcategory_id
          );
        });
  
        console.log("Selected Categories:", selected); // Debug selected categories
        setSelectedCategories(selected);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching role data:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load role details.",
        });
        navigate("/roles");
        setLoading(false);
      });
  
    // Fetch categories
    axios
      .get(`${API_BASE_URL}/api/getCatecory`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        setCategories(response.data);
        setCategoriesLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching categories:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load categories.",
        });
        setCategoriesLoading(false);
      });
  }, [id, navigate, token]);
  
  const handleParentCheckboxChange = (categoryId) => {
    setSelectedCategories((prevState) => {
      const isCategorySelected = categoryId in prevState;

      if (isCategorySelected) {
        const { [categoryId]: _, ...remaining } = prevState;
        return remaining;
      } else {
        const category = categories.find((cat) => cat.id === categoryId);
        const subcategoryIds =
          category.subcategories?.map((sub) => sub.subcategory_id) || [];
        return { ...prevState, [categoryId]: subcategoryIds };
      }
    });
  };

  const handleChildCheckboxChange = (categoryId, subcategoryId) => {
    setSelectedCategories((prevState) => {
      const selectedSubcategories = prevState[categoryId] || [];
      const isSubcategorySelected = selectedSubcategories.includes(
        subcategoryId
      );

      if (isSubcategorySelected) {
        const updatedSubcategories = selectedSubcategories.filter(
          (id) => id !== subcategoryId
        );
        if (updatedSubcategories.length === 0) {
          const { [categoryId]: _, ...remaining } = prevState;
          return remaining;
        } else {
          return { ...prevState, [categoryId]: updatedSubcategories };
        }
      } else {
        return {
          ...prevState,
          [categoryId]: [...selectedSubcategories, subcategoryId],
        };
      }
    });
  };
    
  const handleSave = () => {
    // Debugging: Log selectedCategories to verify the data
    console.log("Selected Categories:", selectedCategories);
  
    // Ensure selectedCategories is an object and handle it properly
    if (typeof selectedCategories !== "object") {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Invalid categories data.",
      });
      return;
    }
  
    // Transform the object into an array of categories with their subcategories
    const categories = Object.keys(selectedCategories).map((categoryId) => ({
      category_id: parseInt(categoryId),  // Convert the category ID to a number
      subcategories: selectedCategories[categoryId],  // The value is an array of subcategory IDs
    }));
  
    // Prepare the payload
    const payload = {
      role_id: id,
      categories: categories,  // The transformed array
    };
  
    // Send the data to the backend
    axios
      .post(`${API_BASE_URL}/api/assignCategoriesToRoleWithSub`, payload)
      .then(() => {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Categories and subcategories assigned successfully!",
        });
        navigate("/admin-user/role-assign");
      })
      .catch((error) => {
        console.error(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to assign categories and subcategories.",
        });
      });
  };
  
  

  // Checking if parent or child is selected
  const isParentChecked = (categoryId) => categoryId in selectedCategories;
  const isChildChecked = (categoryId, subcategoryId) =>
    selectedCategories[categoryId]?.includes(subcategoryId) || false;

  if (loading || categoriesLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      {/* <Typography variant="h5" gutterBottom>
        Edit Role: {role.name}
      </Typography> */}
      <AppBar position="static" sx={{ backgroundColor: '#2196f3' }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
             Edit Role: {role.name}
          </Typography>       
        </Toolbar>
      </AppBar>
      <Card>
        <CardContent>
          <Box sx={{ maxHeight: 400, overflowY: "auto" }}>           

          {categories.map((category) => (
              <div key={category.id}>
                {/* Parent Checkbox */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isParentChecked(category.id)}
                      onChange={() => handleParentCheckboxChange(category.id)}
                    />
                  }
                  label={category.name}
                />
                {/* Child Checkboxes */}
                {category.subcategories?.map((subcategory) => (
                  <div key={subcategory.subcategory_id} style={{ marginLeft: "20px" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isChildChecked(category.id, subcategory.subcategory_id)}
                          onChange={() =>
                            handleChildCheckboxChange(category.id, subcategory.subcategory_id)
                          }
                        />
                      }
                      label={subcategory.subcategory_name}
                    />
                  </div>
                ))}
              </div>
            ))}
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              sx={{ mr: 2 }}
              onClick={handleSave}
              disabled={loading || categoriesLoading}
            >
              Save
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate("/roles")}
            >
              Cancel
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default EditRolePermission;
